import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useToast } from '../hooks/use-toast';
import {
  ArrowLeft, Loader2, Search, Send, CheckCircle2, RefreshCw, MessageSquare, X as XIcon,
  Circle, User as UserIcon, ExternalLink, Filter,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_CFG = {
  open:    { label: 'Open',    color: 'bg-amber-100 text-amber-800',  dot: 'bg-amber-500' },
  replied: { label: 'Replied', color: 'bg-sky-100 text-sky-800',       dot: 'bg-sky-500' },
  closed:  { label: 'Closed',  color: 'bg-neutral-100 text-neutral-600', dot: 'bg-neutral-400' },
};

const CATEGORY_CFG = {
  general:  'General',
  order:    'Order issue',
  payment:  'Payment',
  account:  'Account',
  feedback: 'Feedback',
};

const AdminTicketsPage = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [tickets, setTickets] = useState([]);
  const [counts, setCounts] = useState({ open: 0, replied: 0, closed: 0, total: 0 });
  const [busy, setBusy] = useState(false);
  const [filter, setFilter] = useState('open');
  const [q, setQ] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [replying, setReplying] = useState(false);

  useEffect(() => {
    if (!loading && (!user || !user.is_admin)) navigate('/');
  }, [user, loading, navigate]);

  const fetchList = useCallback(async () => {
    setBusy(true);
    try {
      const params = {};
      if (filter !== 'all') params.status = filter;
      if (q.trim()) params.q = q.trim();
      const { data } = await axios.get(`${API}/admin/tickets`, { params });
      setTickets(data.tickets);
      setCounts(data.counts);
    } catch { toast({ title: 'Failed to load tickets', variant: 'destructive' }); }
    finally { setBusy(false); }
  }, [filter, q, toast]);

  useEffect(() => { if (user?.is_admin) fetchList(); }, [user, fetchList]);

  const fetchDetail = async (id) => {
    setSelectedId(id);
    setDetail(null);
    setReplyText('');
    try {
      const { data } = await axios.get(`${API}/admin/tickets/${id}`);
      setDetail(data);
    } catch { toast({ title: 'Failed to load ticket', variant: 'destructive' }); }
  };

  const sendReply = async (closeAfter = false) => {
    if (!detail || !replyText.trim()) return;
    setReplying(true);
    try {
      await axios.post(`${API}/admin/tickets/${detail.id}/reply`, { message: replyText.trim(), close_ticket: closeAfter });
      toast({ title: 'Reply sent', description: `Emailed to ${detail.user_email}` });
      setReplyText('');
      fetchDetail(detail.id);
      fetchList();
    } catch (e) {
      toast({ title: 'Reply failed', description: e?.response?.data?.detail || 'Try again', variant: 'destructive' });
    } finally { setReplying(false); }
  };

  const setStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/admin/tickets/${id}`, { status });
      toast({ title: `Marked ${status}` });
      fetchList();
      if (detail?.id === id) fetchDetail(id);
    } catch { toast({ title: 'Update failed', variant: 'destructive' }); }
  };

  const selectedTicket = useMemo(() => tickets.find(t => t.id === selectedId), [tickets, selectedId]);

  if (loading || !user?.is_admin) return <div className="max-w-4xl mx-auto p-16 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-neutral-400" /></div>;

  return (
    <div className="min-h-screen bg-neutral-50">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <div>
            <Link to="/admin" className="inline-flex items-center gap-1 text-xs text-neutral-500 hover:text-neutral-800 mb-2" data-testid="back-to-admin"><ArrowLeft className="w-3 h-3" /> Back to admin</Link>
            <h1 className="font-display text-3xl font-bold tracking-tight">Support tickets</h1>
            <p className="text-sm text-neutral-500 mt-1">
              <b className="text-neutral-800">{counts.open}</b> open · <b className="text-neutral-800">{counts.replied}</b> replied · <b className="text-neutral-800">{counts.closed}</b> closed · {counts.total} total
            </p>
          </div>
          <Button onClick={fetchList} variant="outline" size="sm" className="gap-2" data-testid="refresh-btn"><RefreshCw className={`w-3.5 h-3.5 ${busy ? 'animate-spin' : ''}`} /> Refresh</Button>
        </div>

        <div className="grid lg:grid-cols-[380px,1fr] gap-4 lg:h-[calc(100vh-200px)]">
          {/* LIST */}
          <div className="bg-white rounded-2xl border border-neutral-200 flex flex-col overflow-hidden">
            <div className="p-3 border-b border-neutral-100 space-y-2">
              <div className="relative">
                <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
                <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Search subject, message, email, ticket #" className="pl-9 h-9 text-sm" data-testid="search-input" />
              </div>
              <div className="flex gap-1">
                {['open', 'replied', 'closed', 'all'].map(f => (
                  <button
                    key={f}
                    onClick={() => setFilter(f)}
                    data-testid={`filter-${f}`}
                    className={`flex-1 px-2 py-1.5 rounded text-[11px] font-semibold transition-all ${filter === f ? 'bg-neutral-900 text-white' : 'text-neutral-500 hover:bg-neutral-100'}`}>
                    {f.charAt(0).toUpperCase() + f.slice(1)}
                    {f !== 'all' && counts[f] > 0 && <span className="ml-1 text-[10px] opacity-70">({counts[f]})</span>}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex-1 overflow-y-auto" data-testid="tickets-list">
              {busy && tickets.length === 0 ? (
                <div className="p-8 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto text-neutral-400" /></div>
              ) : tickets.length === 0 ? (
                <div className="p-8 text-center text-sm text-neutral-500">
                  <MessageSquare className="w-8 h-8 mx-auto text-neutral-300 mb-2" />
                  No tickets match your filters.
                </div>
              ) : tickets.map(t => (
                <button
                  key={t.id}
                  onClick={() => fetchDetail(t.id)}
                  data-testid={`ticket-row-${t.ticket_number}`}
                  className={`w-full text-left p-3 border-b border-neutral-100 hover:bg-neutral-50 transition-colors ${selectedId === t.id ? 'bg-violet-50/50 border-l-4 border-l-[#8247E5]' : ''}`}>
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${STATUS_CFG[t.status]?.dot || 'bg-neutral-300'}`} />
                      <span className="text-[10px] font-mono font-bold text-neutral-500">{t.ticket_number || t.id.slice(-6).toUpperCase()}</span>
                    </div>
                    <span className="text-[10px] text-neutral-400 shrink-0">{new Date(t.updated_at || t.created_at).toLocaleDateString()}</span>
                  </div>
                  <div className="font-semibold text-sm text-neutral-900 truncate mb-0.5">{t.subject}</div>
                  <div className="text-xs text-neutral-500 truncate">{t.user_email}</div>
                  <div className="flex items-center gap-2 mt-1.5">
                    <span className="text-[9px] font-bold uppercase tracking-widest bg-neutral-100 text-neutral-600 px-1.5 py-0.5 rounded">{CATEGORY_CFG[t.category] || t.category}</span>
                    {t.reply_count > 0 && <span className="text-[10px] text-neutral-400">{t.reply_count} reply</span>}
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* DETAIL */}
          <div className="bg-white rounded-2xl border border-neutral-200 flex flex-col overflow-hidden">
            {!selectedId ? (
              <div className="flex-1 flex items-center justify-center p-12 text-center">
                <div>
                  <MessageSquare className="w-10 h-10 mx-auto text-neutral-300 mb-3" />
                  <div className="font-semibold text-neutral-700">Select a ticket to view</div>
                  <div className="text-sm text-neutral-500 mt-1 max-w-sm">Replies go out from support@tokengate.in and are logged against the ticket.</div>
                </div>
              </div>
            ) : !detail ? (
              <div className="flex-1 flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-neutral-400" /></div>
            ) : (
              <div className="flex flex-col flex-1 overflow-hidden">
                {/* Header */}
                <div className="p-5 border-b border-neutral-100 flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <span className={`text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded ${STATUS_CFG[detail.status]?.color || 'bg-neutral-100'}`} data-testid="detail-status-badge">{STATUS_CFG[detail.status]?.label || detail.status}</span>
                      <span className="text-[11px] font-mono font-bold text-neutral-500">{detail.ticket_number || detail.id.slice(-6).toUpperCase()}</span>
                      <span className="text-[10px] font-semibold uppercase tracking-widest bg-neutral-100 text-neutral-600 px-2 py-0.5 rounded">{CATEGORY_CFG[detail.category] || detail.category}</span>
                    </div>
                    <h2 className="font-display text-xl font-bold" data-testid="detail-subject">{detail.subject}</h2>
                    <div className="text-xs text-neutral-500 mt-1 flex items-center gap-3 flex-wrap">
                      <span className="flex items-center gap-1"><UserIcon className="w-3 h-3" /> {detail.user_name || 'Anonymous'} · {detail.user_email}</span>
                      <span>Opened {new Date(detail.created_at).toLocaleString()}</span>
                      {detail.order_id && <Link to={`/order/${detail.order_id}`} className="flex items-center gap-1 text-[#8247E5]">Order {detail.order_id.slice(-6)} <ExternalLink className="w-3 h-3" /></Link>}
                    </div>
                  </div>
                  <div className="flex gap-2 shrink-0">
                    {detail.status !== 'closed' ? (
                      <Button variant="outline" size="sm" onClick={() => setStatus(detail.id, 'closed')} data-testid="close-btn"><CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Close</Button>
                    ) : (
                      <Button variant="outline" size="sm" onClick={() => setStatus(detail.id, 'open')} data-testid="reopen-btn"><Circle className="w-3.5 h-3.5 mr-1" /> Reopen</Button>
                    )}
                    <Button variant="ghost" size="sm" onClick={() => { setSelectedId(null); setDetail(null); }} data-testid="close-detail-btn"><XIcon className="w-4 h-4" /></Button>
                  </div>
                </div>

                {/* Thread */}
                <div className="flex-1 overflow-y-auto p-5 space-y-3 bg-neutral-50/40">
                  {/* Original message */}
                  <div className="bg-white rounded-xl border border-neutral-200 p-4" data-testid="original-message">
                    <div className="flex items-center justify-between mb-2 text-xs text-neutral-500">
                      <div className="flex items-center gap-1.5"><UserIcon className="w-3 h-3" /><b className="text-neutral-800">{detail.user_name || detail.user_email}</b></div>
                      <span>{new Date(detail.created_at).toLocaleString()}</span>
                    </div>
                    <div className="text-sm text-neutral-800 whitespace-pre-wrap leading-relaxed">{detail.message}</div>
                  </div>
                  {/* Admin replies */}
                  {(detail.replies || []).map((r) => (
                    <div key={r.id} className="bg-violet-50/70 rounded-xl border border-violet-200 p-4 ml-6" data-testid="admin-reply">
                      <div className="flex items-center justify-between mb-2 text-xs text-violet-700">
                        <div className="flex items-center gap-1.5"><Send className="w-3 h-3" /><b>{r.author_name || 'Support'}</b> <span className="text-violet-500">(from support@)</span></div>
                        <span>{new Date(r.created_at).toLocaleString()}</span>
                      </div>
                      <div className="text-sm text-neutral-800 whitespace-pre-wrap leading-relaxed">{r.message}</div>
                    </div>
                  ))}
                </div>

                {/* Reply composer */}
                <div className="border-t border-neutral-100 p-4 bg-white">
                  <textarea
                    value={replyText}
                    onChange={e => setReplyText(e.target.value)}
                    placeholder="Type your reply to the user…  This email will go from support@tokengate.in"
                    className="w-full h-24 border border-neutral-200 rounded-lg px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-[#8247E5] focus:border-transparent"
                    data-testid="reply-textarea"
                  />
                  <div className="mt-2 flex items-center justify-between gap-2 flex-wrap">
                    <div className="text-[11px] text-neutral-400">Reply from <b className="text-neutral-600">support@tokengate.in</b> · User will be emailed instantly</div>
                    <div className="flex gap-2">
                      <Button variant="outline" disabled={replying || !replyText.trim()} onClick={() => sendReply(true)} data-testid="reply-close-btn" className="gap-1.5">
                        <CheckCircle2 className="w-3.5 h-3.5" /> Reply & close
                      </Button>
                      <Button disabled={replying || !replyText.trim()} onClick={() => sendReply(false)} data-testid="reply-send-btn" className="bg-[#8247E5] hover:bg-[#6d33cc] gap-1.5">
                        {replying ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />} Send reply
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminTicketsPage;
